/**
 * ========================================
 * Login Page - 관리자 로그인
 * ========================================
 * 모던하고 트랜디한 디자인으로 개선
 * - Framer Motion 애니메이션 적용
 * - React Icons 트랜디한 아이콘 사용
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { 
  HiSparkles, 
  HiShieldCheck, 
  HiUser, 
  HiLockClosed, 
  HiArrowRight,
  HiExclamation,
  HiCheckCircle
} from 'react-icons/hi';
import { adminAuth } from '../services/api.service';

export default function Login() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await adminAuth.login(data.username, data.password);

      if (response.success) {
        // 로그인 성공 시 대시보드로 이동
        navigate('/dashboard');
      } else {
        setErrorMessage('로그인 실패: 알 수 없는 오류');
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage(
        error.response?.data?.error || '로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-theme-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-theme-accent rounded-full mix-blend-multiply filter blur-xl opacity-10"
          animate={{
            x: [0, 30, 0],
            y: [0, -50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-theme-secondary rounded-full mix-blend-multiply filter blur-xl opacity-10"
          animate={{
            x: [0, -20, 0],
            y: [0, 20, 0],
            scale: [1, 0.9, 1],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            delay: 2,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-theme-primary rounded-full mix-blend-multiply filter blur-xl opacity-10"
          animate={{
            x: [0, 20, 0],
            y: [0, -30, 0],
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            delay: 4,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="max-w-7xl w-full grid md:grid-cols-2 gap-4 relative z-10 px-2">
        {/* Left Side - Branding & Info */}
        <motion.div
          className="hidden md:flex flex-col justify-center text-white space-y-6"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.div
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full w-fit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiSparkles className="w-5 h-5" />
              <span className="text-sm font-medium whitespace-nowrap">관리자 시스템</span>
            </motion.div>
            
            <motion.h1
              className="text-5xl font-bold leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              팔팔구구<br />
              <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                메타보맵
              </span>
            </motion.h1>
            
            <motion.p
              className="text-xl text-theme-text-secondary korean-text-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              위치기반 자기장 명소 탐방 가이드 서비스
            </motion.p>
          </motion.div>

          <motion.div
            className="space-y-4 pt-4 border-t border-white/20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <p className="text-theme-text-secondary korean-text-relaxed leading-relaxed">
              제주도의 특별한 자기장 명소를 탐방하고, 스마트폰으로 자기장 수치를 직접 측정해보세요.
            </p>
            
            <motion.div
              className="flex items-start space-x-3 bg-theme-surface/50 backdrop-blur-sm p-4 rounded-lg border border-theme-primary/20"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <HiExclamation className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
              <ul className="space-y-2 text-xs text-theme-text-secondary">
                <li className="flex items-start whitespace-nowrap">
                  <span className="mr-2 flex-shrink-0">•</span>
                  <span>본 앱은 의료 기기가 아니며, 어떠한 의학적 조언, 진단, 치료 정보도 제공하지 않습니다.</span>
                </li>
                <li className="flex items-start whitespace-nowrap">
                  <span className="mr-2 flex-shrink-0">•</span>
                  <span>자기장 수치와 건강 사이의 관계에 대해 본 앱은 어떠한 주장도 하지 않습니다.</span>
                </li>
                <li className="flex items-start whitespace-nowrap">
                  <span className="mr-2 flex-shrink-0">•</span>
                  <span>건강 관련 문의는 반드시 의료 전문가와 상담하시기 바랍니다.</span>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Right Side - Login Form */}
        <div className="flex items-center justify-center">
          <motion.div
            className="w-full max-w-md bg-theme-surface backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-theme-primary/20"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {/* Mobile Header */}
            <motion.div
              className="md:hidden text-center mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-3xl font-bold text-theme-text mb-2 whitespace-nowrap">
                팔팔구구 <span className="bg-gradient-to-r from-theme-primary to-theme-secondary bg-clip-text text-transparent">메타보맵</span>
              </h1>
              <p className="text-sm text-theme-text-secondary whitespace-nowrap">관리자 로그인</p>
            </motion.div>

            {/* Desktop Header */}
            <motion.div
              className="hidden md:block text-center mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.div
                className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-theme-primary to-theme-secondary rounded-2xl mb-4 shadow-lg"
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
              >
                <HiShieldCheck className="w-8 h-8 text-white" />
              </motion.div>
              <h2 className="text-2xl font-bold text-theme-text mb-2 whitespace-nowrap">관리자 로그인</h2>
              <p className="text-sm text-theme-text-secondary whitespace-nowrap">시스템에 접속하세요</p>
            </motion.div>

            <motion.form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {/* Username */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <label className="block text-sm font-semibold text-theme-text-secondary mb-2 whitespace-nowrap">
                  관리자 ID
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <HiUser className="h-5 w-5 text-theme-text-secondary" />
                  </div>
                  <input
                    type="text"
                    {...register('username', {
                      required: '관리자 ID를 입력해주세요',
                    })}
                    className="w-full pl-10 pr-4 py-3 border-2 border-theme-primary/30 rounded-xl focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all duration-200 bg-theme-background text-theme-text focus:bg-theme-surface"
                    placeholder="admin"
                    autoComplete="username"
                  />
                </div>
                {errors.username && (
                  <motion.p
                    className="mt-2 text-sm text-red-600 flex items-center"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <HiExclamation className="w-4 h-4 mr-1" />
                    {errors.username.message}
                  </motion.p>
                )}
              </motion.div>

              {/* Password */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <label className="block text-sm font-semibold text-theme-text-secondary mb-2 whitespace-nowrap">
                  비밀번호
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <HiLockClosed className="h-5 w-5 text-theme-text-secondary" />
                  </div>
                  <input
                    type="password"
                    {...register('password', {
                      required: '비밀번호를 입력해주세요',
                    })}
                    className="w-full pl-10 pr-4 py-3 border-2 border-theme-primary/30 rounded-xl focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all duration-200 bg-theme-background text-theme-text focus:bg-theme-surface"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </div>
                {errors.password && (
                  <motion.p
                    className="mt-2 text-sm text-red-600 flex items-center"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <HiExclamation className="w-4 h-4 mr-1" />
                    {errors.password.message}
                  </motion.p>
                )}
              </motion.div>

              {/* Error Message */}
              {errorMessage && (
                <motion.div
                  className="p-4 bg-red-50 border-2 border-red-200 rounded-xl"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-start">
                    <HiExclamation className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-800">{errorMessage}</p>
                  </div>
                </motion.div>
              )}

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-gradient-to-r from-theme-primary to-theme-secondary hover:from-indigo-500 hover:to-pink-500 text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <motion.svg
                      className="h-5 w-5 text-white mr-3"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </motion.svg>
                    로그인 중...
                  </span>
                ) : (
                  <span className="flex items-center justify-center whitespace-nowrap">
                    로그인
                    <HiArrowRight className="ml-2 w-5 h-5" />
                  </span>
                )}
              </motion.button>
            </motion.form>
          </motion.div>
        </div>
      </div>

      {/* Copyright Footer */}
      <motion.footer
        className="absolute bottom-0 left-0 right-0 py-4 px-4 text-center z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="inline-flex flex-col items-center gap-2 bg-white/10 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/20 shadow-lg"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="flex items-center gap-2">
              <HiShieldCheck className="w-4 h-4 text-white/80" />
              <span className="text-xs font-semibold text-white/90">
                저작권 및 재산권 소유자
              </span>
            </div>
            <span className="text-xs font-medium text-white/80">
              Copyright &copy; {new Date().getFullYear()} Siddhi Kim (
              <a 
                href="mailto:siddhakim@gmail.com" 
                className="text-white/90 hover:text-white underline transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                siddhakim@gmail.com
              </a>
              ). All rights reserved.
            </span>
          </motion.div>
        </div>
      </motion.footer>
    </div>
  );
}
